/* =========================================================================== *\
 __      __.__     ___________           .__    ____ ________  ________.________
/  \    /  \__| ___\__    ___/___   ____ |  |__/_   /   __   \/   __   \   ____/
\   \/\/   /  |/    \|    |_/ __ \_/ ___\|  |  \|   \____    /\____    /____  \ 
 \        /|  |   |  \    |\  ___/\  \___|   Y  \   |  /    /    /    //       \
  \__/\  / |__|___|  /____| \___  >\___  >___|  /___| /____/    /____//______  /
       \/          \/           \/     \/     \/                             \/ 
\* =========================================================================== */

import React, { useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

// @mui material components
import Icon from '@mui/material/Icon';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import TableHead from '@mui/material/TableHead';
import Grid from '@mui/material/Grid';
import TableContainer from '@mui/material/TableContainer';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';

// Material Dashboard 2 PRO React examples
import { APP_STATE } from 'services/Define';
import { FetchData } from 'services/Request';
import * as Func from 'services/Function';

const initParent = {
  id: '',
  code: '',
  remark: '',
  type_id: '',
  supplier_id: '',
  discount_type: 'B',
  discount_value: '',
  discount_percent: '',
  shipping: '',
  discount_unit: 'Y',
  approve: 'N',
  vat_id: '2',
  status: 'edit',
  deposit: '',
  print_url: '',
  created_at: '',
  updated_at: '',
};

const initPhoto = {
  id: '',
  code: '',
  sku: '',
  name: '',
  image: '',
};

const SaleOrderList = forwardRef(({ parentId }, ref) => {
  const mod = 'sale_order_list';
  const app = useSelector((state) => state[APP_STATE]);
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const [record, setRecord] = useState([]);
  const [campaign, setCampaign] = useState([]);
  const [notify, setNotify] = useState(null);
  const [loading, setLoading] = useState(true);
  const [parent, setParent] = useState(initParent);
  const [modalPhoto, setModalPhoto] = useState(false);
  const [photo, setPhoto] = useState(initPhoto);

  const view = () => {
    FetchData('POST', `/${mod}`, app.token, {
      lang: app.lang,
      ref_id: parentId,
    }).then((result) => {
      if (result.status === 'ok') {
        setRecord(result.data);
        setCampaign(result.campaign);
        setParent(result.parent);
        setLoading(false);
      }
    });
  };

  const renderRecord = () => {
    return (
      <TableContainer sx={{ boxShadow: 0, minHeight: 200 }}>
        <Table aria-label='simple table'>
          <TableHead>
            <TableRow>
              <TableCell align='center' width={20}>
                No.
              </TableCell>
              <TableCell align='center' width={40}>
                รูป
              </TableCell>
              <TableCell>สินค้า</TableCell>
              <TableCell>โปรโมชั่น</TableCell>
              <TableCell align='right' width={90} className='nowrap'>
                จำนวน
              </TableCell>
              <TableCell align='right' width={90} className='nowrap'>
                ราคา
              </TableCell>
              <TableCell align='right' width={90} className='nowrap'>
                มูลค่า
              </TableCell>
              <TableCell align='right' width={90}>
                ภาษี
              </TableCell>
              <TableCell align='right' width={90}>
                รวม
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={9} align='center' className='color-default'>
                  กำลังโหลดข้อมูล ...
                </TableCell>
              </TableRow>
            ) : null}
            {record.map((row) => (
              <TableRow key={row.id}>
                <TableCell align='center'>{row.no}</TableCell>
                <TableCell
                  align='center'
                  onClick={() => {
                    setPhoto(row);
                    setModalPhoto(true);
                  }}
                >
                  {Func.imageDisplay(row)}
                </TableCell>
                <TableCell>
                  <MDBox className='nowrap'>
                    {row.code} <span className='color-default'>{row.sku}</span>
                  </MDBox>
                  <MDBox className='cut' width={200}>
                    {row.name}
                  </MDBox>
                </TableCell>
                <TableCell>
                  <MDBox className='nowrap'>{row.campaign_code}</MDBox>
                  <MDBox className='cut' width={300}>
                    {row.campaign_name}
                  </MDBox>
                </TableCell>
                <TableCell align='right'>
                  <MDBox className='color-primary'>{Func.numberDisplay(row.quantity)}</MDBox>
                  <MDBox>{row.unit_name}</MDBox>
                </TableCell>
                <TableCell align='right'>{row.price}</TableCell>
                <TableCell align='right'>{Func.numberDisplay(row.amount, 2)}</TableCell>
                <TableCell align='right'>{Func.numberDisplay(row.vat_amount, 2)}</TableCell>
                <TableCell align='right'>{Func.numberDisplay(row.total, 2)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const renderCampaign = () => {
    return (
      <TableContainer sx={{ boxShadow: 0, minHeight: 300 }}>
        <Table aria-label='simple table'>
          <TableHead>
            <TableRow>
              <TableCell>โปรโมชั่น</TableCell>
              <TableCell>ประเภท</TableCell>
              <TableCell align='right' width={90}>
                ส่วนลด
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={2} align='center' className='color-default'>
                  กำลังโหลดข้อมูล ...
                </TableCell>
              </TableRow>
            ) : null}
            {campaign.map((row) => (
              <TableRow key={row.id}>
                <TableCell>
                  <MDBox className='nowrap'>{row.code}</MDBox>
                  <MDBox>{row.name}</MDBox>
                </TableCell>
                <TableCell>
                  <MDBox>{row.promotion_type}</MDBox>
                  <MDBox className='nowrap'>{row.type_name}</MDBox>
                </TableCell>
                <TableCell align='right'>
                  {row.discount > 0 ? Func.numberDisplay(row.discount, 2) : '-'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const renderSummary = () => {
    const data = (title, value, clr = 'dark') => {
      return (
        <TableRow>
          <TableCell align='right'>
            <MDTypography variant='h5'>{title}</MDTypography>
          </TableCell>
          <TableCell align='right' width='200'>
            <MDTypography variant='h5' color={clr}>
              {value}
            </MDTypography>
          </TableCell>
          <TableCell align='right' width={50}>
            <MDTypography variant='h5'>บาท</MDTypography>
          </TableCell>
        </TableRow>
      );
    };

    return (
      <Table aria-label='summary table'>
        <TableBody>
          {data('มูลค่าสินค้า', Func.numberDisplay(parent.amount, 2))}
          {data(`ภาษีมูลค่าเพิ่ม ${parent.vat_value}%`, Func.numberDisplay(parent.vat, 2))}
          {data('รับเงินสด', Func.numberDisplay(parent.money_receive, 2))}
          {data('เงินทอน', Func.numberDisplay(parent.money_change, 2))}
          {data('จ่ายเงินสด', Func.numberDisplay(parent.money_cash, 2))}
          {data('จ่ายเงินโอน', Func.numberDisplay(parent.money_transfer, 2))}
          {data('จ่ายบัตร', Func.numberDisplay(parent.money_credit, 2))}
          {data('ส่วนลดคูปอง', Func.numberDisplay(parent.discount_coupon, 2))}
          {data('ขอส่วนลด', Func.numberDisplay(parent.discount_approve, 2))}
          {data('ส่วนลดคืนสินค้า', Func.numberDisplay(parent.return_total, 2))}
          {data('ส่วนลดโปรโมชั่น', Func.numberDisplay(parent.discount, 2))}
          {data('มูลค่าก่อนลด', Func.numberDisplay(parent.total, 2), 'info')}
          {data('ส่วนลดรวม', Func.numberDisplay(parent.discount_all, 2), 'error')}
          {data('รวมทั้งสิ้น', Func.numberDisplay(parent.nettotal, 2), 'success')}
        </TableBody>
      </Table>
    );
  };

  const renderList = () => {
    return (
      <MDBox>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <MDBox className='card' p={2}>
              <MDBox mb={1}>
                <MDTypography variant='h5'>รายการขาย</MDTypography>
              </MDBox>
              {renderRecord()}
            </MDBox>
          </Grid>
          <Grid item md={12} lg={6}>
            {campaign.length > 0 ? (
              <MDBox className='card' p={2}>
                <MDBox mb={1}>
                  <MDTypography variant='h5'>แคมเปญ</MDTypography>
                </MDBox>
                {renderCampaign()}
              </MDBox>
            ) : null}
          </Grid>
          <Grid item md={12} lg={6}>
            <TableContainer sx={{ boxShadow: 0, mt: 2 }}>{renderSummary()}</TableContainer>
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  const renderPhoto = () => {
    return (
      <Dialog
        open={modalPhoto}
        maxWidth='sm'
        keepMounted
        aria-describedby='dialog-image'
        fullWidth
        onClose={() => setModalPhoto(false)}
      >
        <DialogTitle>
          {photo.code} <span className='color-default'>{photo.sku}</span>
          <IconButton
            aria-label='close'
            onClick={() => setModalPhoto(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Icon>close</Icon>
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <MDBox mb={1}>
            <MDTypography variant='body'>{photo.name}</MDTypography>
          </MDBox>
          <MDBox
            component='img'
            src={photo.image}
            alt=''
            borderRadius='lg'
            width='100%'
            position='relative'
            sx={{ boxShadow: 0 }}
          />
        </DialogContent>
      </Dialog>
    );
  };

  const renderLoading = () => {
    return (
      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color='inherit' />
      </Backdrop>
    );
  };

  useImperativeHandle(ref, () => {
    return {
      view,
    };
  });

  useEffect(() => {
    i18n.changeLanguage(app.lang);

    if (parentId !== '') view();
  }, [parentId]);

  return (
    <div>
      {renderList()}
      {renderLoading()}
      {renderPhoto()}
      {notify}
    </div>
  );
});

SaleOrderList.propTypes = {
  parentId: PropTypes.string.isRequired,
};

export default SaleOrderList;
